import {
    InputAdornment,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { DensityRecommendationForm } from '/@/types/density-recommendation';

export const UserDensityTable = ({
    densityValue,
    nitrogenValue,
    yieldValue,
    userYieldValue,
    isLoading,
    userNitrogen,
    userDensity,
    unitProviderProps,
}: {
    densityValue: number;
    nitrogenValue: number;
    yieldValue: number;
    userYieldValue: number;
    isLoading: boolean;
    userNitrogen: number;
    userDensity: number;
    unitProviderProps?: any;
}) => {
    const units = useUnitProvider(unitProviderProps);

    const parsedUserDensityValue = units.toCornDensity(userDensity, 2);

    return (
        <Stack spacing={2} width='100%'>
            <Table sx={{ border: (theme: any) => `1px solid ${theme.palette.divider}` }}>
                <TableHead>
                    <TableRow>
                        <TableCell width='33%' />
                        <TableCell width='33%' sx={{ bgcolor: '#F5F5F5' }}>
                            {_('Recomendada')}
                        </TableCell>
                        <TableCell width='33%'>{_('Habitual')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell width='33%'>{_('Densidad')}</TableCell>
                        <TableCell width='33%' sx={{ bgcolor: '#F5F5F5' }}>
                            {units.toCornDensity(densityValue, 2).toString()}
                        </TableCell>
                        <TableCell width='33%'>{parsedUserDensityValue.toString()}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell width='33%'>{_('Nitrógeno')}</TableCell>
                        <TableCell width='33%' sx={{ bgcolor: '#F5F5F5' }}>
                            {`${nitrogenValue?.toFixed(2)} kg/ha`}
                        </TableCell>
                        <TableCell width='33%'>{`${userNitrogen?.toFixed(2)} kg/ha`}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell width='33%'>{_('Rendimiento')}</TableCell>
                        <TableCell width='33%' sx={{ bgcolor: '#F5F5F5' }}>
                            {units.toCornYield(yieldValue, 2).toString()}
                        </TableCell>
                        <TableCell width='33%'>
                            {isLoading || !userYieldValue ? (
                                <Skeleton variant='text' width='100%' />
                            ) : (
                                units.toCornYield(userYieldValue, 2).toString()
                            )}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Stack direction='row' justifyContent='center' sx={{ background: '#E7F8FF' }} p={2} width='100%'>
                <Typography variant='body2' color='primary' fontWeight='bold'>
                    {`${_('Diferencial de rendimiento')} `}
                    {isLoading || !userYieldValue ? (
                        <Skeleton variant='text' width='80px' sx={{ display: 'inline-flex' }} />
                    ) : (
                        units.toCornYield(yieldValue - userYieldValue, 2).toString()
                    )}
                </Typography>
            </Stack>
        </Stack>
    );
};

const UserDensityModal = ({
    open,
    onClose,
    userYieldValue,
    densityValue,
    yieldValue,
    nitrogenValue,
    isLoading,
    userNitrogen,
    userDensity,
    form,
    readOnly = false,
}: {
    open: boolean;
    onClose?: () => void;

    // User Inputs
    form: UseFormReturn<DensityRecommendationForm>;

    // Results
    userYieldValue: number;
    densityValue: number;
    yieldValue: number;
    nitrogenValue: number;
    userNitrogen: number;
    userDensity: number;
    isLoading: boolean;
    readOnly: boolean;
}) => {
    const units = useUnitProvider();

    // Local state for display values (what user sees and types)
    const [displayDensity, setDisplayDensity] = useState('');
    const [displayNitrogen, setDisplayNitrogen] = useState('');

    const parsedUserDensityValue = units.toCornDensity(userDensity, 2);

    // Update display values when form values change (e.g., when units change)
    useEffect(() => setDisplayDensity(parsedUserDensityValue.value.toString()), [parsedUserDensityValue.value]);

    useEffect(() => {
        setDisplayNitrogen(userNitrogen.toString());
    }, [userNitrogen]);

    // Handle density input changes
    const handleDensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayDensity(e.target.value);
    };

    const handleDensityBlur = () => {
        const numericValue = Number(displayDensity);
        if (!Number.isNaN(numericValue) && numericValue >= 0) {
            form.setValue('userDensity', units.fromCornDensity(numericValue));
        }
    };

    // Handle nitrogen input changes
    const handleNitrogenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayNitrogen(e.target.value);
    };

    const handleNitrogenBlur = () => {
        const numericValue = Number(displayNitrogen);
        if (!Number.isNaN(numericValue) && numericValue >= 0) {
            form.setValue('userNitrogen', numericValue);
        }
    };

    return (
        <ModalV2
            title={_('Compará la densidad recomendada con la habitual')}
            open={open}
            onClose={onClose}
            maxWidth='md'
        >
            <Stack alignItems='start' spacing={3} width='100%'>
                <Typography color='primary.mui.inactive'>
                    {readOnly
                        ? _('Datos habituales del productor:')
                        : _('Ingresá los datos habituales del productor para ver la diferencia')}
                </Typography>
                <Grid2 container spacing={2} alignItems='center' mx={'-8px !important'}>
                    <Grid2 xs={6}>
                        <Stack spacing={2} alignItems='start'>
                            <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                {_('Densidad habitual')}
                            </Typography>
                            <TextField
                                type='number'
                                inputProps={{ min: 0 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {units.getUnit('density', 'corn')}
                                        </InputAdornment>
                                    ),
                                }}
                                value={displayDensity}
                                onChange={handleDensityChange}
                                onBlur={handleDensityBlur}
                                error={!!form.formState.errors.userDensity}
                                helperText={form.formState.errors.userDensity?.message}
                                disabled={readOnly}
                            />
                        </Stack>
                    </Grid2>
                    <Grid2 xs={6}>
                        <Stack spacing={2} alignItems='start'>
                            <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                {_('Nitrógeno')}
                            </Typography>
                            <TextField
                                type='number'
                                inputProps={{ min: 0 }}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>{'kg/ha'}</InputAdornment>,
                                }}
                                value={displayNitrogen}
                                onChange={handleNitrogenChange}
                                onBlur={handleNitrogenBlur}
                                error={!!form.formState.errors.userNitrogen}
                                helperText={form.formState.errors.userNitrogen?.message}
                                disabled={readOnly}
                            />
                        </Stack>
                    </Grid2>
                </Grid2>
                <UserDensityTable
                    densityValue={densityValue}
                    nitrogenValue={nitrogenValue}
                    yieldValue={yieldValue}
                    userYieldValue={userYieldValue}
                    isLoading={isLoading}
                    userNitrogen={userNitrogen}
                    userDensity={userDensity}
                />
            </Stack>
        </ModalV2>
    );
};

export default UserDensityModal;
